// ---------------------------------------------------------------------------
// Weekly focus-coverage derivation — §5.4 of the UI improvement
// recommendations. The calendar page needs a one-line readout of "this
// week" so the exec can see how much of the working week is meetings,
// how much is protected focus, and what's left unallocated.
//
// Baseline working hours default to 40h/week (Mon-Fri 08:00-16:00 with
// a lunch break). Keeping the baseline tunable means the number can
// later be pulled from preferences without a call-site rewrite.
// ---------------------------------------------------------------------------

export interface FocusCoverageEvent {
  startTime: string;
  endTime: string;
  isAllDay?: boolean;
}

export interface FocusCoverageBlock {
  date: string; // yyyy-MM-dd
  startTime: string; // HH:mm
  endTime: string; // HH:mm
}

export interface FocusCoverage {
  meetingMinutes: number;
  focusMinutes: number;
  freeMinutes: number;
  baselineMinutes: number;
  meetingPercent: number;
  focusPercent: number;
  freePercent: number;
  overloaded: boolean; // meetings + focus exceeds baseline
}

function clampToRange(
  startMs: number,
  endMs: number,
  rangeStartMs: number,
  rangeEndMs: number,
): number {
  const start = Math.max(startMs, rangeStartMs);
  const end = Math.min(endMs, rangeEndMs);
  if (end <= start) return 0;
  return Math.round((end - start) / 60000);
}

function blockStartAndEndMs(b: FocusCoverageBlock): { start: number; end: number } | null {
  const [startH, startM] = b.startTime.split(':').map((n) => parseInt(n, 10));
  const [endH, endM] = b.endTime.split(':').map((n) => parseInt(n, 10));
  if ([startH, startM, endH, endM].some(Number.isNaN)) return null;
  const start = new Date(b.date);
  start.setHours(startH, startM, 0, 0);
  const end = new Date(b.date);
  end.setHours(endH, endM, 0, 0);
  if (end <= start) return null;
  return { start: start.getTime(), end: end.getTime() };
}

export function calculateFocusCoverage(
  events: FocusCoverageEvent[],
  focusBlocks: FocusCoverageBlock[],
  weekStart: Date,
  weekEnd: Date,
  baselineMinutesPerWeek: number = 2400, // 40h
): FocusCoverage {
  const rangeStart = weekStart.getTime();
  const rangeEnd = weekEnd.getTime();

  let meetingMinutes = 0;
  for (const e of events) {
    if (e.isAllDay) continue;
    const startMs = new Date(e.startTime).getTime();
    const endMs = new Date(e.endTime).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) continue;
    meetingMinutes += clampToRange(startMs, endMs, rangeStart, rangeEnd);
  }

  let focusMinutes = 0;
  for (const b of focusBlocks) {
    const range = blockStartAndEndMs(b);
    if (!range) continue;
    focusMinutes += clampToRange(range.start, range.end, rangeStart, rangeEnd);
  }

  const freeMinutes = Math.max(0, baselineMinutesPerWeek - meetingMinutes - focusMinutes);
  const baseline = baselineMinutesPerWeek || 1;
  const meetingPercent = Math.round((meetingMinutes / baseline) * 100);
  const focusPercent = Math.round((focusMinutes / baseline) * 100);
  const freePercent = Math.max(0, 100 - meetingPercent - focusPercent);

  return {
    meetingMinutes,
    focusMinutes,
    freeMinutes,
    baselineMinutes: baselineMinutesPerWeek,
    meetingPercent,
    focusPercent,
    freePercent,
    overloaded: meetingMinutes + focusMinutes > baselineMinutesPerWeek,
  };
}

export function minutesToHours(minutes: number): string {
  const hours = Math.round(minutes / 6) / 10;
  return `${hours}h`;
}
